import React, { useState, useEffect } from 'react';
import { Card, Tag, Typography, Spin, Alert, Button, Divider, Collapse, Space, Row, Col, List, Table, Tooltip, Modal, Input } from 'antd';
import { ReloadOutlined, InfoCircleOutlined, DownOutlined, RightOutlined, FileOutlined, FileImageOutlined, FilePdfOutlined, VideoCameraOutlined, CheckOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { TicketDetail as TicketDetailType, TicketDetailResponse, TicketFile } from '../types/ticket';
import { apiService } from '../services/api';
import moment from 'moment';
import TicketHistory from './TicketHistory';
import FileViewer from './FileViewer';
import FileAttachment from './FileAttachment';
import WarrantyHistory from './WarrantyHistory';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;

interface TicketDetailProps {
  ticketId: string;
  token?: string | null;
  onBack?: () => void;
  showAcceptSuccess?: boolean;
  showRejectSuccess?: boolean;
  showRejectModal?: boolean;
}

const TicketDetail: React.FC<TicketDetailProps> = ({ ticketId, token, onBack, showAcceptSuccess = false, showRejectSuccess = false, showRejectModal = false }) => {
  const [ticketData, setTicketData] = useState<TicketDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fabExpanded, setFabExpanded] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [actionType, setActionType] = useState<'accept' | 'reject' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionNote, setRejectionNote] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(showAcceptSuccess || showRejectSuccess);
  const [successType, setSuccessType] = useState<'accept' | 'reject' | null>(showAcceptSuccess ? 'accept' : showRejectSuccess ? 'reject' : null);
  const [rejectNoteError, setRejectNoteError] = useState<string | null>(null);

  // Check if floating action buttons should be shown
  const shouldShowFab = ticketData?.ticket && 
    ticketData.ticket.ticketStatus === 2 && // WAIT_PROCESS
    ticketData.ticket.processingStatus === 2; // WAIT_PROCESS

  const fetchTicketDetail = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTicketDetail(ticketId, token);
      setTicketData(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicketDetail();
  }, [ticketId, token]);

  // Auto-show reject modal if showRejectModal is true
  useEffect(() => {
    if (showRejectModal && ticketData?.ticket) {
      setActionType('reject');
      setConfirmModalVisible(true);
    }
  }, [showRejectModal, ticketData]);

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  const getStatusColor = (status: number) => {
    const statusMap: { [key: number]: string } = {
      1: 'default',   // WAIT_RECEIPT
      2: 'processing', // WAIT_PROCESS
      3: 'processing', // PROCESSING
      4: 'success',    // COMPLETED
      5: 'error',      // REJECTED
    };
    return statusMap[status] || 'default';
  };

  const getStatusText = (status: number) => {
    const statusMap: { [key: number]: string } = {
      1: 'Waiting for Receipt',
      2: 'Waiting for Processing',
      3: 'Processing',
      4: 'Completed',
      5: 'Rejected',
    };
    return statusMap[status] || 'Unknown';
  };

  const getProcessingStatusColor = (status: number) => {
    const statusMap: { [key: number]: string } = {
      1: 'default',   // WAIT_APPROVE
      2: 'processing', // WAIT_PROCESS
      3: 'processing', // PROCESSING
      4: 'success',    // PROCESSED
      5: 'warning',    // WAIT_CONFIRM
      6: 'default',    // PENDING
      7: 'success',    // COMPLETED
      8: 'error',      // CANCELLED
      9: 'processing', // PROCESSING_AGAIN
    };
    return statusMap[status] || 'default';
  };

  const getProcessingStatusText = (status: number) => {
    const statusMap: { [key: number]: string } = {
      1: 'Waiting for Approval',
      2: 'Waiting for Processing',
      3: 'Processing',
      4: 'Processed',
      5: 'Waiting for Confirmation',
      6: 'Pending',
      7: 'Completed',
      8: 'Cancelled',
      9: 'Processing Again',
    };
    return statusMap[status] || 'Unknown';
  };

  const toggleFab = () => {
    setFabExpanded(!fabExpanded);
  };

  const handleActionClick = (type: 'accept' | 'reject') => {
    setActionType(type);
    setRejectionNote(''); // Reset note when opening modal
    setConfirmModalVisible(true);
    setFabExpanded(false);
  };

  const handleConfirmAction = async () => {
    if (!actionType || !ticketData) return;
    if (actionType === 'reject' && !rejectionNote.trim()) {
      setRejectNoteError('Rejection reason is required.');
      return;
    }
    setRejectNoteError(null);
    try {
      setActionLoading(true);
      if (actionType === 'accept') {
        await apiService.acceptTicket(ticketId, token);
      } else {
        await apiService.rejectTicket(ticketId, token, rejectionNote);
      }
      await fetchTicketDetail();
      setConfirmModalVisible(false);
      setRejectionNote('');
      const actionText = actionType === 'accept' ? 'accepted' : 'rejected';
      const baseUrl = window.location.origin;
      const tokenParam = token ? `?t=${token}` : '';
      const successParam = actionType === 'accept' ? '&success=accept' : '&success=reject';
      const redirectUrl = `${baseUrl}/in-app/ticket/${ticketId}${tokenParam}${successParam}`;
      window.location.href = redirectUrl;
    } catch (error: any) {
      console.error(`Error ${actionType}ing ticket:`, error);
      const errorMessage = error.response?.data?.message || `Failed to ${actionType} ticket`;
      console.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
        <div className="loading-text">Loading ticket details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '16px' }}
      />
    );
  }

  if (!ticketData || !ticketData.ticket) {
    return (
      <Alert
        message="Not Found"
        description="Ticket not found"
        type="warning"
        showIcon
        style={{ margin: '16px' }}
      />
    );
  }

  const ticket = ticketData.ticket;

  // Custom panel header
  const panelHeader = (title: string) => (
    <div className="card-title">{title}</div>
  );

  // Define interface for table data
  interface TableDataItem {
    key: string;
    field: string;
    value: React.ReactNode;
  }

  // Prepare data for general information table
  const generalInfoData: TableDataItem[] = [
    {
      key: '1',
      field: 'Request Date',
      value: moment(ticket.requestDate).format('YYYY/MM/DD HH:mm'),
    },
    {
      key: '2',
      field: 'Request Type',
      value: ticket.requestTypeName,
    },
    {
      key: '3',
      field: 'Description',
      value: ticket.description,
    },
    {
      key: '4',
      field: 'Processing Status',
      value: (
        <Tag color={getProcessingStatusColor(ticket.processingStatus)}>
          {getProcessingStatusText(ticket.processingStatus)}
        </Tag>
      ),
    },
    {
      key: '5',
      field: 'Person in charge',
      value: ticket.processBranch?.accountName || 'N/A',
    },
  ];

  // Prepare data for customer information table
  const customerInfoData: TableDataItem[] = [
    {
      key: '1',
      field: 'Customer Name',
      value: ticket.customer.customerName,
    },
    {
      key: '2',
      field: 'Phone Number',
      value: ticket.customer.phoneNumber || 'N/A',
    },
  ];

  // Prepare data for device information table
  const deviceInfoData: TableDataItem[] = [
    {
      key: '1',
      field: 'Serial',
      value: ticket.device.serialNumber,
    },
  ];

  if (ticket.device.model) {
    deviceInfoData.push({
      key: '2',
      field: 'Model',
      value: ticket.device.model,
    });
  }

  if (ticket.device.size) {
    deviceInfoData.push({
      key: '3',
      field: 'Size',
      value: ticket.device.size.toString(),
    });
  }

  // Prepare data for solution plan table
  const getSolutionData = (): TableDataItem[] => {
    if (!ticket.solution) return [];
    
    const data: TableDataItem[] = [
      {
        key: '1',
        field: 'Description',
        value: ticket.solution.description,
      },
      {
        key: '2',
        field: 'Processing Date',
        value: ticket.solution.processDate 
          ? moment(ticket.solution.processDate).format('YYYY/MM/DD HH:mm') 
          : 'N/A',
      },
    ];
    
    if (ticket.solution.fileAttachments && ticket.solution.fileAttachments.length > 0) {
      data.push({
        key: '3',
        field: 'Attachments',
        value: (
          <FileAttachment 
            files={ticket.solution.fileAttachments}
            token={token}
            showLabel={false}
          />
        ),
      });
    }
    
    return data;
  };

  // Prepare data for error report table
  const getErrorReportData = (): TableDataItem[] => {
    if (!ticket.errorReport) return [];
    
    const data: TableDataItem[] = [
      {
        key: '1',
        field: 'Inspection Item',
        value: ticket.errorReport.description,
      },
      {
        key: '2',
        field: 'Result',
        value: ticket.errorReport.result,
      },
    ];
    
    if (ticket.errorReport.fileAttachments && ticket.errorReport.fileAttachments.length > 0) {
      data.push({
        key: '3',
        field: 'Attachments',
        value: (
          <FileAttachment 
            files={ticket.errorReport.fileAttachments}
            token={token}
            showLabel={false}
          />
        ),
      });
    }
    
    return data;
  };

  // Table columns configuration
  const tableColumns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      render: (text: string) => <Text className="detail-label">{text}</Text>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
    },
  ];

  return (
    <div className="ticket-detail-container">
      {/* Header */}
      <div className="app-header">
        <div className="app-header-logo">
          <img src="/logo192.png" alt="Citymind Logo" className="app-logo" />
          <Text className="app-header-title">Citymind CS</Text>
        </div>
        <div className="app-header-actions">
          <Title level={5} className="app-header-subtitle">
            Ticket Details
          </Title>
          <Button 
            type="text" 
            icon={<ReloadOutlined />} 
            className="app-header-button"
            onClick={fetchTicketDetail}
          />
        </div>
      </div>

      {/* Content */}
      <div className="app-content">
        {/* Success Message */}
        {showSuccessMessage && (
          <Alert
            message={successType === 'accept' ? "Ticket Accepted Successfully" : "Ticket Rejected Successfully"}
            description={successType === 'accept' ? "The ticket has been accepted and is now being processed." : "The ticket has been rejected."}
            type="success"
            showIcon
            closable
            onClose={() => setShowSuccessMessage(false)}
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* Ticket ID and Status */}
        <div className="ticket-header">
          <div className="ticket-id">
            <InfoCircleOutlined className="ticket-icon" />
            <Text className="ticket-id-text">{ticket.ticketId}</Text>
          </div>
          <div className="ticket-status">
            <Tag 
              className="ticket-status-tag" 
              color={getStatusColor(ticket.ticketStatus)}
            >
              {getStatusText(ticket.ticketStatus)}
            </Tag>
          </div>
        </div>

        {/* General Information */}
        <Collapse 
          defaultActiveKey={['general']} 
          className="detail-collapse"
          expandIconPosition="end"
        >
          <Panel header={panelHeader("General Information")} key="general">
            <Table
              dataSource={generalInfoData}
              columns={tableColumns}
              pagination={false}
              showHeader={false}
              size="small"
              className="detail-table"
            />
          </Panel>
        </Collapse>

        {/* Customer Information */}
        <Collapse 
          defaultActiveKey={['customer']} 
          className="detail-collapse"
          expandIconPosition="end"
        >
          <Panel header={panelHeader("Customer Information")} key="customer">
            <Table
              dataSource={customerInfoData}
              columns={tableColumns}
              pagination={false}
              showHeader={false}
              size="small"
              className="detail-table"
            />
          </Panel>
        </Collapse>

        {/* Device Information */}
        <Collapse 
          defaultActiveKey={['device']} 
          className="detail-collapse"
          expandIconPosition="end"
        >
          <Panel header={panelHeader("Device Information")} key="device">
            <Table
              dataSource={deviceInfoData}
              columns={tableColumns}
              pagination={false}
              showHeader={false}
              size="small"
              className="detail-table"
            />
          </Panel>
        </Collapse>

        {/* Solution Plan */}
        {ticket.solution && (
          <Collapse 
            defaultActiveKey={['solution']} 
            className="detail-collapse"
            expandIconPosition="end"
          >
            <Panel header={panelHeader("Solution Plan")} key="solution">
              <Table
                dataSource={getSolutionData()}
                columns={tableColumns}
                pagination={false}
                showHeader={false}
                size="small"
                className="detail-table"
              />
            </Panel>
          </Collapse>
        )}

        {/* Error Report */}
        {ticket.errorReport && (
          <Collapse 
            defaultActiveKey={['error']} 
            className="detail-collapse"
            expandIconPosition="end"
          >
            <Panel header={panelHeader("Error Report")} key="error">
              <Table
                dataSource={getErrorReportData()}
                columns={tableColumns}
                pagination={false}
                showHeader={false}
                size="small"
                className="detail-table"
              />
            </Panel>
          </Collapse>
        )}

        {/* Attachments */}
        {ticket.attachments && ticket.attachments.length > 0 && (
          <Collapse 
            defaultActiveKey={['attachments']} 
            className="detail-collapse"
            expandIconPosition="end"
          >
            <Panel header={panelHeader(`Attachments (${ticket.attachments.length})`)} key="attachments">
              <FileViewer files={ticket.attachments} token={token} />
            </Panel>
          </Collapse>
        )}

        {/* Process History */}
        <TicketHistory histories={ticketData.histories} token={token} />

        {/* Warranty History */}
        <WarrantyHistory 
          serialNumber={ticket.device.serialNumber} 
          token={token} 
        />

        {/* Floating Action Button */}
        {shouldShowFab && (
          <div className="floating-action-buttons">
            {fabExpanded && (
              <>
                <div className="fab-button-container">
                  <Button 
                    type="primary" 
                    shape="circle" 
                    icon={<CheckOutlined />} 
                    className="fab-button fab-accept"
                    onClick={() => handleActionClick('accept')}
                  >
                    <span className="fab-text">Accept</span>
                  </Button>
                </div>
                <div className="fab-button-container">
                  <Button 
                    danger 
                    type="primary" 
                    shape="circle" 
                    icon={<CloseOutlined />} 
                    className="fab-button fab-reject"
                    onClick={() => handleActionClick('reject')}
                  >
                    <span className="fab-text">Reject</span>
                  </Button>
                </div>
              </>
            )}
            <Button 
              type="primary" 
              shape="circle" 
              icon={fabExpanded ? <CloseOutlined /> : <PlusOutlined />} 
              className="fab-button fab-main"
              onClick={toggleFab}
              size="large"
            />
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        title={`${actionType === 'accept' ? 'Accept' : 'Reject'} Ticket`}
        open={confirmModalVisible}
        onOk={handleConfirmAction}
        onCancel={() => {
          setConfirmModalVisible(false);
          setRejectionNote('');
          setRejectNoteError(null);
        }}
        confirmLoading={actionLoading}
        okText={actionType === 'accept' ? 'Accept' : 'Reject'}
        cancelText="Cancel"
        okButtonProps={{
          disabled: actionType === 'reject' && !rejectionNote.trim(),
          style: {
            backgroundColor: actionType === 'accept' ? '#0E76FF' : '#dc3545',
            borderColor: actionType === 'accept' ? '#0E76FF' : '#dc3545',
            color: '#fff',
            marginTop: 16,
          },
        }}
        cancelButtonProps={{
          style: {
            marginTop: 16,
          },
        }}
      >
        <p>Are you sure you want to {actionType} this ticket?</p>
        {actionType === 'reject' && (
          <div style={{ marginTop: '16px' }}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Reason for rejection <span style={{ color: 'red' }}>*</span>:
            </Text>
            <TextArea
              placeholder="Enter the reason for rejection..."
              rows={4}
              value={rejectionNote}
              onChange={(e) => {
                setRejectionNote(e.target.value);
                if (e.target.value.trim()) setRejectNoteError(null);
              }}
              maxLength={500}
              showCount
            />
            {rejectNoteError && (
              <div style={{ color: 'red', marginTop: 4 }}>{rejectNoteError}</div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TicketDetail;